import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { AUTH, NAV } from "@/constants/testIds";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard, Building2, Users, CalendarClock, FolderArchive,
  Award, GraduationCap, ShieldCheck, LogOut, Globe, UserCog, FileType2, KeyRound, PenLine, ClipboardCheck, ScanLine, Mail,
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/", icon: LayoutDashboard, key: "dashboard", tid: NAV.dashboard, roles: ["super_admin", "operatore", "cliente"] },
  { to: "/companies", icon: Building2, key: "companies", tid: NAV.companies, roles: ["super_admin", "operatore", "cliente"] },
  { to: "/workers", icon: Users, key: "workers", tid: NAV.workers, roles: ["super_admin", "operatore", "cliente"] },
  { to: "/deadlines", icon: CalendarClock, key: "deadlines", tid: NAV.deadlines, roles: ["super_admin", "operatore", "cliente"] },
  { to: "/attestati", icon: ScanLine, key: "attestati", tid: "nav-attestati", roles: ["super_admin", "operatore", "cliente"] },
  { to: "/documents", icon: FolderArchive, key: "documents", tid: NAV.documents, roles: ["super_admin", "operatore", "cliente"] },
  { to: "/certificates", icon: Award, key: "certificates", tid: NAV.certificates, roles: ["super_admin", "operatore", "cliente"] },
  { to: "/dvr", icon: ClipboardCheck, key: "dvr", tid: "nav-dvr", roles: ["super_admin", "operatore"] },
  { to: "/courses", icon: GraduationCap, key: "courses", tid: NAV.courses, roles: ["super_admin", "operatore"] },
  { to: "/templates", icon: FileType2, key: "templates", tid: "nav-templates", roles: ["super_admin", "operatore"] },
  { to: "/signatures", icon: PenLine, key: "signatures", tid: "nav-signatures", roles: ["super_admin", "operatore"] },
  { to: "/nominations", icon: Mail, key: "nominations", tid: "nav-nominations", roles: ["super_admin", "operatore"] },
  { to: "/users", icon: UserCog, key: "users", tid: NAV.users, roles: ["super_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  const items = NAV_ITEMS.filter((i) => i.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[hsl(24,10%,10%)] text-[hsl(60,20%,96%)] flex flex-col">
        <div className="px-5 pt-6 pb-6 flex items-center gap-3 border-b border-white/5">
          <div className="bg-white rounded-md px-2 py-1.5 shadow-sm">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_attestguard/artifacts/3wuvsw34_FORMATO-WEB-NERO.jpg"
              alt="Sicurgarda"
              className="h-7 w-auto"
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest opacity-70">Scadenze</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {items.map(({ to, icon: Icon, key, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={tid}
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{t(key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="text-xs opacity-70 mb-2">{t("hello")},</div>
          <div className="text-sm font-medium truncate">{user.name || user.email}</div>
          <div className="text-[11px] uppercase tracking-widest text-primary/90 mt-0.5">{t(`role_${user.role}`)}</div>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-open"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest bg-white/5 hover:bg-white/10 rounded-full py-2 btn-lift"
          >
            <KeyRound className="w-3.5 h-3.5" /> {t("change_password")}
          </button>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid={AUTH.logoutBtn}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-widest bg-white/10 hover:bg-white/15 rounded-full py-2 btn-lift"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-background/95 backdrop-blur hdr-border">
          <div className="px-8 h-16 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {t("welcome")} · <span className="text-foreground font-medium">{t("app_name")}</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setLang(lang === "it" ? "en" : "it")}
                data-testid={AUTH.langSwitch}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground btn-lift px-2 py-1 rounded-md"
              >
                <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
              </button>
            </div>
          </div>
        </header>
        <div className="p-8">
          {children}
        </div>
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
